import { type productValidator, type productValidatorType } from '@eduzz-product-manager/validators';
import type z from 'zod';
import { create } from 'zustand';
import { useShallow } from 'zustand/shallow';

import { DEFAULT_MAXIMUM_LATE_PAYMENTS } from '@/env';

import { authStore } from './auth';

export type PaymentType = 'oneTime' | 'open' | 'free' | 'subscription';
export type BillingCycleType = 'one_month' | 'three_months' | 'six_months' | 'twelve_months' | 'custom';

export type UpgradeFeatureType = z.infer<typeof productValidator.shape.upgradeSales>[number];

export type ProductData = productValidatorType & {
  upgradeSales: UpgradeFeatureType[];
  draft: boolean;
  linksWereSent?: boolean;
  iaGenerated: Record<string, boolean>;
  producer: {
    native: boolean;
    document: string;
    zipCode: string;
    state: string;
    city: string;
    cityId: string;
    streetName: string;
    streetNumber: string;
    district: string;
    complement?: string;
    cellPhone: string;
    fiscalTerm: boolean;
    useTerm: boolean;
  };
};

type ProductStore = {
  setFields: (data: Partial<ProductData>) => void;
  setIaField: (field: string, value: boolean) => void;
} & ProductData;

export const initialState: ProductData = {
  id: crypto.randomUUID(),
  userId: '',
  title: '',
  email: '',
  description: '',
  draft: true,
  linksWereSent: false,
  image: '',
  categories: [],
  deliverables: [],
  payment: {
    price: 0,
    trialEnabled: false,
    activationFeeEnabled: false,
    type: 'oneTime',
    methods: []
  },
  upgradeSales: [],
  producer: {
    native: false,
    document: '',
    zipCode: '',
    state: '',
    city: '',
    cityId: '',
    streetName: '',
    streetNumber: '',
    district: '',
    complement: '',
    cellPhone: '',
    fiscalTerm: false,
    useTerm: false
  },

  iaGenerated: {}
};

export const productStore = create<ProductStore>()((set, _get) => ({
  ...initialState,

  setIaField: (field: string, value: boolean) => {
    set(state => ({
      iaGenerated: {
        ...state.iaGenerated,
        [field]: value
      }
    }));
  },

  setFields: (fields: Partial<ProductData>) => {
    const userId = fields.userId || authStore.getState().currentUser?.id || '';
    set(state => ({ ...state, ...fields, userId }));
    set(state => {
      if (state.payment.type === 'free') {
        state.payment.methods = [];
      }

      if (state.payment.type !== 'subscription') {
        delete state.payment.billingCycle;
        delete state.payment.recurrenceCount;
        delete state.payment.maximumLatePayments;
      }

      if (state.payment.type === 'subscription') {
        state.payment.maximumLatePayments = state.payment.maximumLatePayments ?? DEFAULT_MAXIMUM_LATE_PAYMENTS;
        state.payment.billingConfig = state.payment.billingConfig || {
          type: 'months',
          value: 1
        };
      }

      return state;
    });
  }
}));

const useProductStore = <T>(selector: (state: ProductStore) => T): T => productStore(useShallow(selector));

export default useProductStore;
